// Series primitive for the base itself: a translucent band over the bars
// between base_start and base_end, the base's ceiling drawn across it, and
// the base's own measurements named on it.
//
// The band is cloned from power_trend_background.js (one band instead of
// many, a configurable colour, no inactive-tail logic). The ceiling and the
// labels are MODEL_BOOK_EVOLUTION.md §6.5 and §6.7:
//
//   - the ceiling is the base high, the level the detector measured
//     base_depth down from, drawn across the base so the shape is legible:
//     a flat-top base retests it, a V-recovery never reaches it. That is the
//     whole difference between the two shapes `VcpDetector.classify_shape`
//     emits, and it was previously a number nobody saw (`ceiling_ratio`).
//   - the labels name the base, not every part of it. The contraction legs
//     already carry their own depths (vcp_markup_overlay's polyline), the
//     pivot has three labelled bands and the `% to/from pivot` readout, so
//     the only parts still unnamed were the base and its ceiling.
//
// Labels carry the MEASUREMENT, never a price: the price axis is right there,
// and a price would need the stock's price_scale plumbed through to avoid
// printing $0.79 as $0.8 on a sub-dollar ASX name.
//
// Everything here is detector-measured, so the ceiling is SOLID — dashed
// belongs to AI-proposed marks and a distinct style stays reserved for
// human-drawn ones (CLAUDE.md VCP rule 6).
//
// Attached via series.attachPrimitive(...) from vcp_annotations_controller.

import { findIndexAtOrAfter, findIndexAtOrBefore } from "./power_trend_background"
import { readTheme, drawLabel, labelFits, readoutReserve, FONT, LABEL_INSET_PX } from "./vcp_markup_overlay"

const DEFAULT_BAR_SPACING = 6
const DEFAULT_COLOR = "rgba(255, 196, 0, 0.18)"  // amber-ish translucent

// Deeper amber than the band so the line reads against its own fill.
export const CEILING_INK = { light: "#b26a00", dark: "#ffb74d" }

// The base summary's centre line, at the top left of the band.
export const SUMMARY_Y = 12
// Gap between the ceiling line and the centre of its "Base high" label.
const CEILING_LABEL_GAP_PX = 9
// The two labels' centre lines must be at least this far apart (11px text,
// plus its halo) or they overprint.
const MIN_LABEL_SEPARATION_PX = 14

// Where "Base high" goes: just above its line, unless that would land on the
// base summary — which it does whenever the ceiling is near the top of the
// pane, and autoscaleInfo makes that common by keeping the base high in
// range: it is often the visible maximum. Then the label goes just below the
// line instead, still beside the thing it names. And when the line itself
// runs through the summary's row, "below" collides too, so it goes to the
// first row clear of the summary.
// The summary starts at the band's left edge but may run past the band to
// the right: a base band at default zoom is ~110px and the summary ~190px,
// so confining it to the band hid it on almost every chart. The space above
// the base is empty up to the pivot readout, which shares this row at the
// top right — that, not the band edge, is the limit.
export function summaryFits(ctx, summary, x, paneWidth) {
  ctx.font = FONT
  return x + ctx.measureText(summary).width <= paneWidth - readoutReserve(ctx)
}

export function ceilingLabelY(lineY) {
  const above = lineY - CEILING_LABEL_GAP_PX
  if (Math.abs(above - SUMMARY_Y) >= MIN_LABEL_SEPARATION_PX) return above

  return Math.max(lineY + CEILING_LABEL_GAP_PX, SUMMARY_Y + MIN_LABEL_SEPARATION_PX)
}

const SHAPE_WORDS = Object.freeze({ vcp: "flat top", v_recovery: "V-recovery" })

// "Base · 32d · −10.5% · flat top". Null when there is nothing to say, so the
// renderer can skip the label rather than draw a bare "Base".
export function baseSummary({ baseLength, baseDepth, shape } = {}) {
  const parts = []
  if (Number(baseLength) > 0) parts.push(`${Math.round(Number(baseLength))}d`)
  if (Number(baseDepth) > 0) parts.push(`−${(Number(baseDepth) * 100).toFixed(1)}%`)
  if (SHAPE_WORDS[shape]) parts.push(SHAPE_WORDS[shape])

  return parts.length === 0 ? null : `Base · ${parts.join(" · ")}`
}

// The summary's forms, longest first, for a renderer that has less room than
// the full one needs: the full summary, then without the "Base ·" prefix (the
// band already says it is the base), then the length and depth alone. The
// shape word goes last because, unlike the measurements, the ceiling line
// beside it already shows it. Empty when there is nothing to say.
export function baseSummaryForms({ baseLength, baseDepth, shape } = {}) {
  const full = baseSummary({ baseLength, baseDepth, shape })
  if (!full) return []

  const body = full.replace(/^Base · /, "")
  const measured = baseSummary({ baseLength, baseDepth })?.replace(/^Base · /, "")
  return [ ...new Set([ full, body, measured ].filter(Boolean)) ]
}

// The band's horizontal extent in MEDIA units, or null when the base does not
// intersect the bars on screen. Integer logicals plus a half-bar margin:
// logicalToCoordinate returns 0 for a fractional logical in v5, so the
// half-bar is added here rather than asking for index ± 0.5.
export function bandExtent(timeScale, data, baseStart, baseEnd) {
  const startIdx = findIndexAtOrAfter(data, baseStart)
  const endIdx = findIndexAtOrBefore(data, baseEnd)
  if (startIdx > endIdx || startIdx >= data.length || endIdx < 0) return null

  const leftCentre = timeScale.logicalToCoordinate(startIdx)
  const rightCentre = timeScale.logicalToCoordinate(endIdx)
  if (leftCentre === null || rightCentre === null) return null

  const halfBar = (timeScale.options().barSpacing || DEFAULT_BAR_SPACING) / 2
  return { x1: leftCentre - halfBar, x2: rightCentre + halfBar }
}

class VcpBaseOverlayRenderer {
  constructor(data) {
    this._data = data
  }

  draw(target) {
    const { chart, baseStart, baseEnd, data, color } = this._data
    if (!chart || !data || data.length === 0 || !baseStart || !baseEnd) return

    target.useBitmapCoordinateSpace(scope => {
      const extent = bandExtent(chart.timeScale(), data, baseStart, baseEnd)
      if (!extent) return

      const pr = scope.horizontalPixelRatio
      scope.context.fillStyle = color
      scope.context.fillRect(extent.x1 * pr, 0, (extent.x2 - extent.x1) * pr, scope.bitmapSize.height)
    })
  }
}

// The ceiling line and the base's labels. Media space, because text drawn in
// bitmap space is sized in device pixels.
class VcpBaseCeilingRenderer {
  constructor(data) {
    this._data = data
  }

  draw(target) {
    const { chart, series, baseStart, baseEnd, baseHigh, data, summaryForms, theme } = this._data
    if (!chart || !series || !data || data.length === 0 || !baseStart || !baseEnd) return
    if (!(Number(baseHigh) > 0)) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const extent = bandExtent(chart.timeScale(), data, baseStart, baseEnd)
      if (!extent) return

      const y = series.priceToCoordinate(Number(baseHigh))
      if (y === null) return

      // Clamp to the pane so a base scrolled half off-screen still draws the
      // part of its ceiling that is visible.
      const x1 = Math.max(0, extent.x1)
      const x2 = Math.min(scope.mediaSize.width, extent.x2)
      if (x2 <= x1) return

      const ink = theme.dark ? CEILING_INK.dark : CEILING_INK.light

      ctx.save()
      ctx.strokeStyle = ink
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.moveTo(x1, y)
      ctx.lineTo(x2, y)
      ctx.stroke()
      ctx.restore()

      // "Base high" only when it fits inside the band (measured, never a
      // fixed minimum), beside its line and clear of the summary; the summary
      // from the band's top left, as far right as the pivot readout allows.
      const width = x2 - x1
      const x = x1 + LABEL_INSET_PX
      if (labelFits(ctx, "Base high", width)) {
        drawLabel(ctx, "Base high", x, ceilingLabelY(y), ink, theme.background)
      }
      // The longest form that fits; none at all only when even the length and
      // depth alone would run into the readout.
      const summary = (summaryForms || []).find(form => summaryFits(ctx, form, x, scope.mediaSize.width))
      if (summary) drawLabel(ctx, summary, x, SUMMARY_Y, ink, theme.background)
    })
  }
}

class VcpBaseOverlayPaneView {
  constructor(data, RendererClass, zOrder) {
    this._data = data
    this._RendererClass = RendererClass
    this._zOrder = zOrder
  }

  update(data) {
    this._data = data
  }

  renderer() {
    return new this._RendererClass(this._data)
  }

  zOrder() {
    return this._zOrder
  }
}

export class VcpBaseOverlay {
  // @param baseStart/baseEnd ISO dates bounding the base
  // @param baseHigh   the base's ceiling — the close the detector anchored on
  // @param baseLength bars in the base, for the summary label
  // @param baseDepth  peak-to-trough fraction, for the summary label
  // @param shape      "vcp" | "v_recovery", for the summary label
  // @param data       the bars on the chart
  constructor({ baseStart, baseEnd, baseHigh, baseLength, baseDepth, shape, data, color = DEFAULT_COLOR }) {
    this._sharedData = {
      chart: null,
      series: null,
      baseStart: baseStart,
      baseEnd: baseEnd,
      baseHigh: baseHigh ?? null,
      data: data || [],
      color: color,
      summaryForms: baseSummaryForms({ baseLength, baseDepth, shape }),
      theme: readTheme()
    }
    this._paneViews = [
      new VcpBaseOverlayPaneView(this._sharedData, VcpBaseOverlayRenderer, "bottom"),
      new VcpBaseOverlayPaneView(this._sharedData, VcpBaseCeilingRenderer, "top")
    ]
    this._attachedParams = null
    // Colours are read on attach and on theme:changed, never per frame:
    // getComputedStyle inside a renderer runs twice per pan/zoom tick.
    this._onThemeChanged = () => {
      this._sharedData.theme = readTheme()
      this.updateAllViews()
      if (this._attachedParams) this._attachedParams.requestUpdate()
    }
  }

  attached(param) {
    this._attachedParams = param
    this._sharedData.chart = param.chart
    this._sharedData.series = param.series
    this._sharedData.theme = readTheme()
    document.addEventListener("theme:changed", this._onThemeChanged)
    this.updateAllViews()
    param.requestUpdate()
  }

  detached() {
    document.removeEventListener("theme:changed", this._onThemeChanged)
    this._sharedData.chart = null
    this._sharedData.series = null
    this._attachedParams = null
  }

  updateAllViews() {
    this._paneViews.forEach(pv => pv.update(this._sharedData))
  }

  paneViews() {
    return this._paneViews
  }

  // Keep the ceiling on screen. A base that sold off hard autoscales to its
  // own range and would clip its own high off the top, which is exactly the
  // chart where the ceiling matters most. Only while the base is in the
  // visible range — scrolled away, the candles get the full scale.
  autoscaleInfo(startTimePoint, endTimePoint) {
    const { baseHigh, data, baseStart, baseEnd } = this._sharedData
    if (!(Number(baseHigh) > 0) || !data || data.length === 0) return null

    const startIdx = findIndexAtOrAfter(data, baseStart)
    const endIdx = findIndexAtOrBefore(data, baseEnd)
    if (typeof endTimePoint === "number" && startIdx > endTimePoint) return null
    if (typeof startTimePoint === "number" && endIdx < startTimePoint) return null

    const high = Number(baseHigh)
    return { priceRange: { minValue: high, maxValue: high } }
  }
}
